import { useState, useEffect, useRef } from "react";

type TrickMode = 'follow' | 'circles' | 'zigzag' | 'spiral' | 'bounce' | 'free';
type ParkingState = 'parked' | 'leaving' | 'active' | 'returning';

const PARKING_POSITION = { x: 100, y: window.innerHeight - 100 };
const CAR_FRONT_DISTANCE = 35; // Distància del davant del cotxe al cursor

export const PorscheCursor = () => {
  const [isActive, setIsActive] = useState(false);
  const [parkingState, setParkingState] = useState<ParkingState>('parked');
  const [position, setPosition] = useState({ ...PARKING_POSITION });
  const [targetPosition, setTargetPosition] = useState({ x: 0, y: 0 });
  const [trail, setTrail] = useState<Array<{ x: number; y: number; rotation: number }>>([]);
  const [rotation, setRotation] = useState(0);
  const [trickMode, setTrickMode] = useState<TrickMode>('follow');
  const [trickTime, setTrickTime] = useState(0);
  const [speedMultiplier, setSpeedMultiplier] = useState(1);
  const [keysPressed, setKeysPressed] = useState({ w: false, a: false, s: false, d: false });
  const velocityRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef<number | undefined>(undefined);

  // Detectar posició inicial del cursor
  useEffect(() => {
    const handleInitialMouseMove = (event: MouseEvent) => {
      setTargetPosition({ x: event.clientX, y: event.clientY });
    };
    
    window.addEventListener("mousemove", handleInitialMouseMove);
    
    return () => {
      window.removeEventListener("mousemove", handleInitialMouseMove);
    };
  }, []);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // No activar si s'està escrivint en un input o textarea
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      const key = event.key.toLowerCase();

      // P activa/desactiva el mode Porsche
      if (key === "p") {
        if (!isActive) {
          // Activar: sortir del pàrquing
          // Capturar posició actual del cursor si encara no s'ha mogut
          const handleCurrentMousePos = (e: MouseEvent) => {
            setTargetPosition({ x: e.clientX, y: e.clientY });
          };
          window.addEventListener("mousemove", handleCurrentMousePos, { once: true });
          
          // Si el mouse ja està en alguna posició, forçar actualització
          const mouseEvent = new MouseEvent('mousemove');
          Object.defineProperty(mouseEvent, 'clientX', { value: window.innerWidth / 2 });
          Object.defineProperty(mouseEvent, 'clientY', { value: window.innerHeight / 2 });
          
          setIsActive(true);
          setParkingState('leaving');
          setTrickMode('follow');
        } else {
          // Desactivar: tornar al pàrquing
          setParkingState('returning');
          setTrickMode('follow');
        }
        return;
      }

      // ArrowUp/ArrowDown per controlar velocitat
      if (isActive) {
        if (event.key === "ArrowUp") {
          setSpeedMultiplier((prev) => Math.min(prev + 0.25, 5));
          return;
        } else if (event.key === "ArrowDown") {
          setSpeedMultiplier((prev) => Math.max(prev - 0.25, 0.25));
          return;
        }
      }

      // Si el mode Porsche està actiu, altres lletres fan trucs
      if (isActive) {
        if (key === "c") {
          // C = Cercles al centre (comença immediatament)
          setTrickMode('circles');
          setTrickTime(0);
        } else if (key === "z") {
          // Z = Zigzag
          setTrickMode('zigzag');
          setTrickTime(0);
        } else if (key === "e") {
          // E = Espiral
          setTrickMode('spiral');
          setTrickTime(0);
        } else if (key === "b") {
          // B = Rebot pels costats
          setTrickMode('bounce');
          setTrickTime(0);
          velocityRef.current = { x: 12, y: 12 };
        } else if (key === "m") {
          // M = Mode lliure (conduir amb WASD)
          setTrickMode('free');
          setTrickTime(0);
          velocityRef.current = { x: 0, y: 0 };
        } else if (key === "f") {
          // F = Tornar a seguir el cursor
          setTrickMode('follow');
          setTrickTime(0);
        }
        
        // Gestió de tecles WASD per mode lliure
        if (trickMode === 'free') {
          if (key === 'w' || key === 'a' || key === 's' || key === 'd') {
            event.preventDefault();
            setKeysPressed(prev => ({ ...prev, [key]: true }));
          }
        }
      }
    };
    
    const handleKeyUp = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (trickMode === 'free' && (key === 'w' || key === 'a' || key === 's' || key === 'd')) {
        setKeysPressed(prev => ({ ...prev, [key]: false }));
      }
    };
    
    window.addEventListener("keydown", handleKeyPress);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [isActive, trickMode]);

  useEffect(() => {
    if (!isActive) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      setTargetPosition({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [isActive]);

  // Animació suavitzada per seguir el cursor o fer trucs
  useEffect(() => {
    if (!isActive) return;

    const animate = () => {
      setTrickTime(prev => prev + 1);

      setPosition((prevPosition) => {
        let targetX = prevPosition.x;
        let targetY = prevPosition.y;
        let dx = 0;
        let dy = 0;

        // Gestionar estats de pàrquing
        if (parkingState === 'leaving') {
          // Sortint del pàrquing cap al cursor
          // Calcular angle cap al cursor per orientar el cotxe
          const angleToCursor = Math.atan2(
            targetPosition.y - prevPosition.y,
            targetPosition.x - prevPosition.x
          );
          
          // Posicionar el cotxe darrere del cursor en direcció oposada
          targetX = targetPosition.x - CAR_FRONT_DISTANCE * Math.cos(angleToCursor);
          targetY = targetPosition.y - CAR_FRONT_DISTANCE * Math.sin(angleToCursor);
          
          const distanceToTarget = Math.sqrt(
            Math.pow(targetX - prevPosition.x, 2) + 
            Math.pow(targetY - prevPosition.y, 2)
          );
          
          if (distanceToTarget < 10) {
            // Ha arribat al cursor, ara està actiu
            setParkingState('active');
          }
        } else if (parkingState === 'returning') {
          // Tornant al pàrquing
          targetX = PARKING_POSITION.x;
          targetY = PARKING_POSITION.y;
          
          const distanceToParking = Math.sqrt(
            Math.pow(targetX - prevPosition.x, 2) + 
            Math.pow(targetY - prevPosition.y, 2)
          );
          
          if (distanceToParking < 5) {
            // Ha arribat al pàrquing, desactivar
            setIsActive(false);
            setParkingState('parked');
            setPosition({ ...PARKING_POSITION });
            return PARKING_POSITION;
          }
        } else if (parkingState === 'active') {
          // Calcular posició objectiu segons el mode de truc
          if (trickMode === 'follow') {
            // Mode normal: seguir el cursor amb el davant apuntant-hi
            // Calcular angle cap al cursor
            const angleToCursor = Math.atan2(
              targetPosition.y - prevPosition.y,
              targetPosition.x - prevPosition.x
            );
            
            // Posicionar el cotxe darrere del cursor
            targetX = targetPosition.x - CAR_FRONT_DISTANCE * Math.cos(angleToCursor);
            targetY = targetPosition.y - CAR_FRONT_DISTANCE * Math.sin(angleToCursor);
          } else if (trickMode === 'circles') {
            // Cercles al centre de la pantalla
            const centerX = window.innerWidth / 2;
            const centerY = window.innerHeight / 2;
            const radius = 150;
            const speed = 0.12 * speedMultiplier;
            const angle = trickTime * speed;
            
            targetX = centerX + Math.cos(angle) * radius;
            targetY = centerY + Math.sin(angle) * radius;
          } else if (trickMode === 'zigzag') {
          // Zigzag horitzontal
          const speed = 10 * speedMultiplier;
          const amplitude = 100;
          const frequency = 0.1 * speedMultiplier;
          
          targetX = prevPosition.x + speed;
          targetY = window.innerHeight / 2 + Math.sin(trickTime * frequency) * amplitude;
          
          // Tornar a l'inici si arriba al final
          if (targetX > window.innerWidth + 100) {
            targetX = -100;
          }
        } else if (trickMode === 'spiral') {
          // Espiral creixent des del centre
          const centerX = window.innerWidth / 2;
          const centerY = window.innerHeight / 2;
          const speed = 0.15 * speedMultiplier;
          const radiusGrowth = 1.2 * speedMultiplier;
          const angle = trickTime * speed;
          const radius = trickTime * radiusGrowth;
          
          targetX = centerX + Math.cos(angle) * radius;
          targetY = centerY + Math.sin(angle) * radius;
          
          // Reiniciar si arriba als límits
          if (radius > Math.max(window.innerWidth, window.innerHeight) / 2) {
            setTrickTime(0);
          }
        } else if (trickMode === 'bounce') {
          // Rebot pels costats de la pantalla
          const maxBounceTime = 300;
          
          if (trickTime > maxBounceTime) {
            setTrickMode('follow');
            setTrickTime(0);
          } else {
            const currentVelX = velocityRef.current.x;
            const currentVelY = velocityRef.current.y;
            
            targetX = prevPosition.x + (currentVelX * speedMultiplier);
            targetY = prevPosition.y + (currentVelY * speedMultiplier);
            
            // Rebot als límits
            if (targetX <= 50 || targetX >= window.innerWidth - 50) {
              velocityRef.current.x = -currentVelX;
            }
            if (targetY <= 50 || targetY >= window.innerHeight - 50) {
              velocityRef.current.y = -currentVelY;
            }
          }
          } else if (trickMode === 'free') {
            // Mode lliure: conduir amb WASD (controls com un cotxe real)
            const acceleration = 1.2 * speedMultiplier;
            const friction = 0.96;
            const maxSpeed = 15 * speedMultiplier;
            const rotationSpeed = 3; // Graus per frame
            
            // W/S: endavant/enrere basant-se en la rotació del cotxe
            // A/D: girar esquerra/dreta
            
            // Convertir rotació actual a radians (la imatge del cotxe apunta a la dreta = 0°)
            const angleRad = rotation * (Math.PI / 180);
            
            // Acceleració/desacceleració endavant/enrere
            let forwardAccel = 0;
            if (keysPressed.w) forwardAccel = acceleration; // Endavant
            if (keysPressed.s) forwardAccel = -acceleration * 0.7; // Enrere (més lent)
            
            // Aplicar acceleració en la direcció del cotxe
            if (forwardAccel !== 0) {
              velocityRef.current.x += Math.cos(angleRad) * forwardAccel;
              velocityRef.current.y += Math.sin(angleRad) * forwardAccel;
            }
            
            // Girar el cotxe (canviar rotació)
            if (keysPressed.a) {
              setRotation(rotation - rotationSpeed); // Girar esquerra
            }
            if (keysPressed.d) {
              setRotation(rotation + rotationSpeed); // Girar dreta
            }
            
            // Aplicar fricció
            velocityRef.current.x *= friction;
            velocityRef.current.y *= friction;
            
            // Limitar velocitat màxima
            const currentSpeed = Math.sqrt(velocityRef.current.x ** 2 + velocityRef.current.y ** 2);
            if (currentSpeed > maxSpeed) {
              const scale = maxSpeed / currentSpeed;
              velocityRef.current.x *= scale;
              velocityRef.current.y *= scale;
            }
            
            // Aplicar velocitat a la posició
            targetX = prevPosition.x + velocityRef.current.x;
            targetY = prevPosition.y + velocityRef.current.y;
            
            // Mantenir dins dels límits de la pantalla
            targetX = Math.max(50, Math.min(window.innerWidth - 50, targetX));
            targetY = Math.max(50, Math.min(window.innerHeight - 50, targetY));
          }
        }

        // Calcular direcció i distància
        dx = targetX - prevPosition.x;
        dy = targetY - prevPosition.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        let newX = prevPosition.x;
        let newY = prevPosition.y;

        // Aplicar moviment suavitzat amb límit de velocitat màxima
        if (distance > 0.5) {
          let speed;
          
          if (parkingState === 'leaving') {
            // Sortint del pàrquing: moviment lent i suau
            speed = 0.03;
          } else if (parkingState === 'returning') {
            // Tornant al pàrquing: moviment normal
            speed = 0.08;
          } else if (trickMode === 'follow') {
            // Mode seguir: suavització extra per evitar cops
            const baseSpeed = 0.06 * speedMultiplier;
            const distanceFactor = Math.min(distance / 300, 1); // Més suau quan està lluny
            speed = baseSpeed + (distanceFactor * 0.03 * speedMultiplier);
            // Limitar velocitat màxima
            speed = Math.min(speed, 0.2);
          } else if (trickMode === 'bounce') {
            // Mode rebot: velocitat fixa
            speed = 1;
          } else if (trickMode === 'free') {
            // Mode lliure: aplicar directament (ja calculat)
            speed = 1;
          } else {
            // Altres modes: interpolació suau cap a la trajectòria
            const interpolationSpeed = 0.15; // Més suau per evitar salts
            speed = interpolationSpeed;
          }
          
          newX = prevPosition.x + dx * speed;
          newY = prevPosition.y + dy * speed;
        }

        // Actualitzar velocitat i rotació
        const deltaX = newX - prevPosition.x;
        const deltaY = newY - prevPosition.y;
        
        velocityRef.current.x = velocityRef.current.x * 0.92 + deltaX * 0.08;
        velocityRef.current.y = velocityRef.current.y * 0.92 + deltaY * 0.08;

        // Calcular angle de rotació (excepte en mode free on es controla manualment)
        if (trickMode !== 'free') {
          const currentSpeed = Math.sqrt(velocityRef.current.x ** 2 + velocityRef.current.y ** 2);
          if (currentSpeed > 0.2 || trickMode !== 'follow') {
            const targetAngle = Math.atan2(dy, dx) * (180 / Math.PI);
            
            let angleDiff = targetAngle - rotation;
            while (angleDiff > 180) angleDiff -= 360;
            while (angleDiff < -180) angleDiff += 360;
            
            const rotationSpeed = 0.5;
            const newRotation = rotation + angleDiff * rotationSpeed;
            setRotation(newRotation);
          }
        }
        
        // Afegir rastre
        const currentSpeed = Math.sqrt(velocityRef.current.x ** 2 + velocityRef.current.y ** 2);
        if (currentSpeed > 0.4 || trickMode !== 'follow') {
          setTrail((prev) => {
            const newTrail = [
              { x: prevPosition.x, y: prevPosition.y, rotation: rotation },
              ...prev.slice(0, 10),
            ];
            return newTrail;
          });
        }

        return { x: newX, y: newY };
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, targetPosition, rotation, trickMode, trickTime, parkingState, speedMultiplier]);

  // Mostrar només si està actiu o en procés de tornar al pàrquing
  if (!isActive && parkingState === 'parked') return null;

  return (
    <>
      {/* Plaça de pàrquing */}
      <div 
        className="fixed z-[9997] pointer-events-none"
        style={{
          left: `${PARKING_POSITION.x}px`,
          top: `${PARKING_POSITION.y}px`,
          transform: 'translate(-50%, -50%)'
        }}
      >
        <div className="relative">
          {/* Requadre de la plaça */}
          <div className="w-24 h-32 border-4 border-yellow-400 rounded-lg bg-yellow-400/10 backdrop-blur-sm flex items-center justify-center">
            <span className="text-6xl font-bold text-yellow-400/50">P</span>
          </div>
          {/* Indicador si està aquí */}
          {parkingState === 'parked' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 bg-green-500/20 border-2 border-green-500 rounded-full animate-pulse" />
            </div>
          )}
        </div>
      </div>

      {/* Rastres de derrapatge */}
      {trail.map((point, index) => (
        <div
          key={index}
          className="fixed pointer-events-none z-[9998] transition-opacity duration-300"
          style={{
            left: `${point.x}px`,
            top: `${point.y}px`,
            transform: `translate(-50%, -50%) rotate(${point.rotation}deg)`,
            opacity: 1 - index / trail.length,
          }}
        >
          <img
            src="/porsche.png"
            alt="Porsche"
            className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
            style={{
              filter: `blur(${index * 0.5}px) brightness(${1 - index * 0.03})`,
            }}
          />
        </div>
      ))}

      {/* Porsche principal */}
      <div
        className="fixed pointer-events-none z-[9999] transition-transform duration-100 ease-out"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        }}
      >
        <img
          src="/porsche.png"
          alt="Porsche"
          className="w-16 h-16 sm:w-20 sm:h-20 object-contain drop-shadow-lg"
        />
      </div>

      {/* Indicador minimalista */}
      <div className="fixed bottom-4 right-4 z-[10000] bg-black/70 backdrop-blur-sm text-white px-4 py-3 rounded-lg text-xs border border-white/20 shadow-lg">
        <div className="flex flex-col gap-3">
          {/* Fila 1: Mode actiu i Velocitat */}
          <div className="flex items-start gap-4">
            {/* Mode actiu */}
            <div className="flex flex-col gap-1">
              <div className="text-[10px] text-white/50 uppercase tracking-wide">Mode</div>
              <div className="flex items-center gap-1.5">
                <span>🏎️</span>
                <span className="text-yellow-300 font-semibold text-xs">
                  {trickMode === 'follow' && 'Seguir'}
                  {trickMode === 'circles' && 'Cercles'}
                  {trickMode === 'zigzag' && 'Zigzag'}
                  {trickMode === 'spiral' && 'Espiral'}
                  {trickMode === 'bounce' && 'Rebot'}
                  {trickMode === 'free' && 'Manual'}
                </span>
              </div>
            </div>
            
            {/* Separador */}
            <div className="w-px h-10 bg-white/20"></div>
            
            {/* Velocitat */}
            <div className="flex flex-col gap-1">
              <div className="text-[10px] text-white/50 uppercase tracking-wide">Velocitat</div>
              <div className="flex items-center gap-1">
                <span className="text-white font-bold text-lg font-mono">{speedMultiplier.toFixed(2)}</span>
                <span className="text-white/40 text-[10px]">×</span>
              </div>
            </div>
          </div>
          
          {/* Separador horitzontal */}
          <div className="h-px bg-white/20"></div>
          
          {/* Fila 2: Controls */}
          <div className="flex flex-col gap-2">
            <div className="text-[10px] text-white/50 uppercase tracking-wide">Controls</div>
            
            {trickMode === 'free' ? (
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 items-center">
                  <div className="flex gap-1">
                    <kbd className="px-2 py-1 bg-yellow-500/20 border border-yellow-500/40 rounded text-xs font-mono text-yellow-300">W</kbd>
                    <kbd className="px-2 py-1 bg-yellow-500/20 border border-yellow-500/40 rounded text-xs font-mono text-yellow-300">A</kbd>
                    <kbd className="px-2 py-1 bg-yellow-500/20 border border-yellow-500/40 rounded text-xs font-mono text-yellow-300">S</kbd>
                    <kbd className="px-2 py-1 bg-yellow-500/20 border border-yellow-500/40 rounded text-xs font-mono text-yellow-300">D</kbd>
                  </div>
                  <span className="text-white/60 text-[10px]">Conduir</span>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="flex gap-1">
                    <kbd className="px-2 py-1 bg-white/10 rounded text-xs font-mono">↑</kbd>
                    <kbd className="px-2 py-1 bg-white/10 rounded text-xs font-mono">↓</kbd>
                  </div>
                  <span className="text-white/60 text-[10px]">Velocitat</span>
                </div>
                <div className="flex gap-2 items-center">
                  <kbd className="px-2 py-1 bg-white/10 rounded text-xs font-mono">F</kbd>
                  <span className="text-white/60 text-[10px]">Tornar a seguir</span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[10px]">
                  <div className="flex items-center gap-1.5">
                    <kbd className="px-1.5 py-0.5 bg-white/10 rounded font-mono text-[10px]">C</kbd>
                    <span className="text-white/60">⭕ Cercles</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <kbd className="px-1.5 py-0.5 bg-white/10 rounded font-mono text-[10px]">Z</kbd>
                    <span className="text-white/60">⚡ Zigzag</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <kbd className="px-1.5 py-0.5 bg-white/10 rounded font-mono text-[10px]">E</kbd>
                    <span className="text-white/60">🌀 Espiral</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <kbd className="px-1.5 py-0.5 bg-white/10 rounded font-mono text-[10px]">B</kbd>
                    <span className="text-white/60">🎾 Rebot</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <kbd className="px-1.5 py-0.5 bg-white/10 rounded font-mono text-[10px]">M</kbd>
                    <span className="text-white/60">🎮 Manual</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <kbd className="px-1.5 py-0.5 bg-white/10 rounded font-mono text-[10px]">F</kbd>
                    <span className="text-white/60">← Seguir</span>
                  </div>
                </div>
                <div className="flex gap-2 items-center pt-1 border-t border-white/10">
                  <div className="flex gap-1">
                    <kbd className="px-2 py-1 bg-white/10 rounded text-xs font-mono">↑</kbd>
                    <kbd className="px-2 py-1 bg-white/10 rounded text-xs font-mono">↓</kbd>
                  </div>
                  <span className="text-white/60 text-[10px]">Ajustar velocitat</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
